// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { toRaw, unref } from 'vue'
import { getAlterations, getClinicalOmics, getCopyNumber, getFusions, getHla, getRnaAnnotation, getVariants } from './getGenomicsData.js'
import { clearCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import { compareDatesDescending } from '@/utils/utils.js'
import { store } from '@/store'
import { getTmbMsi } from './getGenomicsData.js'

const useNewOmicsApi = store.getters.featureToggles.useNewOmicsApi

// TODO: This wrapper, and everything associated with supporting both Old and
// New Omics APIs, including the feature flag, is temporary. The Old interface
// is pending removal
export const loadAllGenomics = async ({
  accessionNum, //needed for loadDisplayData only, since we use the display alteration for /trial data
  alterations,
  controller, // New only
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  getTreatmentsTrialsFirstAlteration,
  getTreatmentsTrials,
  hlaTypes,
  loadingGenomics,
  msi,
  multiComparison,
  rnaAnnotations,
  sampleId, //needed for loadAllGenomics only, for the omics API
  spinnerOn,
  tierButtonClick,
  tmb,
  treatments,
  trials,
  variants,
}) => {
  if (!useNewOmicsApi) {
    loadAllGenomicsOld({
      accessionNum,
      alterations,
      copyNumberVars,
      displayAlterations1,
      displayAlterations2,
      displayAlterations3,
      fusions,
      getTreatmentsTrialsFirstAlteration,
      getTreatmentsTrials,
      hlaTypes,
      loadingGenomics,
      msi,
      multiComparison,
      rnaAnnotations,
      sampleId,
      spinnerOn,
      tierButtonClick,
      tmb,
      treatments,
      trials,
      variants,
    })
    return
  }
  loadAllGenomicsNew({
    accessionNum,
    alterations,
    controller,
    copyNumberVars,
    displayAlterations1,
    displayAlterations2,
    displayAlterations3,
    fusions,
    getTreatmentsTrialsFirstAlteration,
    getTreatmentsTrials,
    hlaTypes,
    loadingGenomics,
    msi,
    multiComparison,
    rnaAnnotations,
    sampleId,
    spinnerOn,
    tierButtonClick,
    tmb,
    treatments,
    trials,
    variants,
  })
}

// NEW
export const loadAllGenomicsNew = async ({
  accessionNum,
  alterations,
  controller,
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  getTreatmentsTrialsFirstAlteration,
  getTreatmentsTrials,
  hlaTypes,
  loadingGenomics,
  msi,
  multiComparison,
  rnaAnnotations,
  sampleId,
  spinnerOn,
  tierButtonClick,
  tmb,
  treatments,
  trials,
  variants,
}) => {
  // const timerStart = performance.now()
  loadingGenomics.value = true
  spinnerOn.value = true
  tierButtonClick(1)
  msi.value = ''
  tmb.value = ''
  const multiComparisonVariants = []
  const multiComparisonFusions = []
  const multiComparisonRna = []
  const multiComparisonCopy = []
  const multiComparisonHla = []

  if (multiComparison.value) {

    let multiComparisonVariantsMerged = []
    let multiComparisonFusionsMerged = []
    let multiComparisonRnaMerged = []
    let multiComparisonCopyMerged = []
    let multiComparisonHlaMerged = []

    // const clinicalTimerStart = performance.now()
    const results = await getClinicalOmics({ controller, sampleId: sampleId.value })
    if (!results.length) { return }
    // const clinicalTimerEnd = performance.now()
    // console.log(`new Clinical genomics fetch call time (single call for multi): ${clinicalTimerEnd - clinicalTimerStart} ms`)

    for (const sample of sampleId.value) {
      const sampleIdString = sample.sample
      const clinicalOmicsResults = results.find((result) => result.samplename === sampleIdString)
      if (!clinicalOmicsResults) { return }

      multiComparisonVariants.push(clinicalOmicsResults.variants)
      multiComparisonVariantsMerged = [...multiComparisonVariantsMerged, ...clinicalOmicsResults.variants]

      const fusionResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'FUSION')
      const copyNumberResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'COPY_NUMBER_VARIATION')
      const rnaAnnotationResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'RNA_ANNOTATION')
      const hlaResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'GENEMARKER')
      const msiResults = clinicalOmicsResults.biomarkers.find((marker) => marker.type === 'MSI')
      const tmbResults = clinicalOmicsResults.biomarkers.find((marker) => marker.type === 'TMB')
      sample.msi = `${msiResults.value || ''} ${msiResults.value_numeric || ''}${msiResults.units || ''}`
      sample.tmb = `${tmbResults.value || ''} ${tmbResults.value_numeric || ''} ${tmbResults.units || ''}`

      multiComparisonFusions.push(fusionResults)
      multiComparisonFusionsMerged = [...multiComparisonFusionsMerged, ...fusionResults]
      multiComparisonRna.push(rnaAnnotationResults)
      multiComparisonRnaMerged = [...multiComparisonRnaMerged, ...rnaAnnotationResults]
      multiComparisonCopy.push(copyNumberResults)
      multiComparisonCopyMerged = [...multiComparisonCopyMerged, ...copyNumberResults]
      multiComparisonHla.push(hlaResults)
      multiComparisonHlaMerged = [...multiComparisonHlaMerged, ...hlaResults]
    }
    variants.value = multiComparisonVariantsMerged
    fusions.value = multiComparisonFusionsMerged
    rnaAnnotations.value = multiComparisonRnaMerged
    copyNumberVars.value = multiComparisonCopyMerged
    hlaTypes.value = multiComparisonHlaMerged

  }
  else {
    const sampleIdString = sampleId.value
    // const clinicalTimerStart = performance.now()
    const results = await getClinicalOmics({ controller, sampleId: sampleIdString })
    // const clinicalTimerEnd = performance.now()
    // console.log(`new Clinical genomics fetch call time (single sample): ${clinicalTimerEnd - clinicalTimerStart} ms`)
    if (!results.length) { return }
    const clinicalOmicsResults = results[0]
    const fusionResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'FUSION')
    const copyNumberResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'COPY_NUMBER_VARIATION')
    const rnaAnnotationResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'RNA_ANNOTATION')
    const hlaResults = clinicalOmicsResults.biomarkers.filter((marker) => marker.type === 'GENEMARKER')
    const msiResults = clinicalOmicsResults.biomarkers.find((marker) => marker.type === 'MSI')
    const tmbResults = clinicalOmicsResults.biomarkers.find((marker) => marker.type === 'TMB')
    variants.value = clinicalOmicsResults.variants
    fusions.value = fusionResults
    rnaAnnotations.value = rnaAnnotationResults
    copyNumberVars.value = copyNumberResults
    hlaTypes.value = hlaResults
    msi.value = `${msiResults.value || ''} ${msiResults.value_numeric || ''}${msiResults.units || ''}`
    tmb.value = `${tmbResults.value || ''} ${tmbResults.value_numeric || ''} ${tmbResults.units || ''}`
  }

  // clear out the alterations array and treatments and trials
  alterations.value = []
  treatments.value = []
  trials.value = []

  // Push to the alterations ref
  unref(variants).forEach(variant => { alterations.value.push(variant) })

  //The sample flagging for multi-sample comparison
  if (multiComparison.value) {
    const flagSamples = (itemList, multiComparisonList, isVariants) => {
      //deduplicate
      if (isVariants) {
        itemList.value = toRaw(unref(itemList)).filter((variant, index, array) =>
          array.findIndex(t => {
            if (variant.protein === '.') {
              return t.gene === variant.gene && t.coding === variant.coding && t.vp === variant.vp
            }
            return t.gene === variant.gene && t.protein === variant.protein && t.vp === variant.vp
          }) === index)
      } else {
        itemList.value = toRaw(unref(itemList)).filter((biomarker, index, array) =>
          array.findIndex(t => t.gene === biomarker.gene && t.gene_secondary === biomarker.gene_secondary
            && t.type === biomarker.type && t.value === biomarker.value) === index)
      }

      for (const item of toRaw(unref(itemList))) {
        multiComparisonList.forEach((array, index) => {
          if (isVariants) {
            if (array.find(e => {
              if (item.protein === '.') {
                return e.gene === item.gene && e.coding === item.coding && item.vp === e.vp
              }
              return e.gene === item.gene && e.protein === item.protein && item.vp === e.vp
            })) {
              item[`Sample_${index + 1}`] = true
            }
          } else {
            if (array.find(e => e.gene === item.gene && e.gene_secondary === item.gene_secondary
              && e.type === item.type && e.value === item.value)) {
              item[`Sample_${index + 1}`] = true
            }
          }
        })
      }
    }

    flagSamples(alterations, multiComparisonVariants, true)
    flagSamples(fusions, multiComparisonFusions, false)
    flagSamples(rnaAnnotations, multiComparisonRna, false)
    flagSamples(copyNumberVars, multiComparisonCopy, false)
    flagSamples(hlaTypes, multiComparisonHla, false)
  }

  await loadDisplayDataNew({
    accessionNum,
    displayAlterations1,
    displayAlterations2,
    displayAlterations3,
    alterations,
    copyNumberVars,
    fusions,
    hlaTypes,
    rnaAnnotations,
  })

  //get the first
  await getTreatmentsTrialsFirstAlteration()

  //get the rest
  await getTreatmentsTrials()
  loadingGenomics.value = false
  spinnerOn.value = false
  // const timerEnd = performance.now()
  // console.log(`loadAllGenomicsNew time: ${timerEnd - timerStart} ms`)
}

const loadDisplayDataNew = async ({
  accessionNum,
  alterations,
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  hlaTypes,
  rnaAnnotations,
}) => {
  clearCenterColumn('genomics')
  displayAlterations1.value = []
  displayAlterations2.value = []
  displayAlterations3.value = []
  const altArray = toRaw(unref(alterations))
  const fusionArray = toRaw(unref(fusions))
  const rnaArray = toRaw(unref(rnaAnnotations))
  const copyArray = toRaw(unref(copyNumberVars))
  const hlaArray = toRaw(unref(hlaTypes))

  const loadDisplayObjects = (rawArray, listType = 'other-non-simple') => {
    for (const item of rawArray) {
      const exon = item.exon || ''
      const priority = item.vp || ''
      const vaf = item.vaf || ''
      const sample1 = item.Sample_1 || false
      const sample2 = item.Sample_2 || false
      const sample3 = item.Sample_3 || false
      const mostRecentAccessionNum = getMostRecentAccessionNum(accessionNum.value, [sample1, sample2, sample3])
      let gene = item.gene
      let displayAlt = ''
      let originalAlt = ''
      let value = ''

      const typeMap = {
        'simple': 'small_variant',
        'fusions': 'fusion',
        'cnv': 'cnv',
        'rna': 'rna_annotation',
        'hla': 'hla',
        'stablity': 'stability',
      }
      const type = typeMap[listType] || ''

      if (listType === 'simple') {
        const alt = item.protein !== '.' ? item.protein.replace('p.', '') : item.coding
        displayAlt = recodeAminoAcid(alt)
        originalAlt = alt
        value = `${gene} ${displayAlt}`
      } else if (listType === 'fusions') {
        gene = 'FUSION'
        displayAlt = originalAlt = value = `${item.gene}-${item.gene_secondary}`
      } else if (listType === 'cnv' && item.value_numeric) {
        displayAlt = `${item.value} ${item.value_numeric}`
        originalAlt = item.value
        value = `${gene} ${originalAlt}`
      } else {
        displayAlt = originalAlt = item.value
        value = `${gene} ${displayAlt}`
      }

      const obj = {
        gene,
        alteration: displayAlt,
        originalAlt,
        exon,
        type,
        priority,
        treatmentsCount: 0,
        trialsCount: 0,
        treatmentRows: [],
        trialRows: [],
        sample1,
        sample2,
        sample3,
        mostRecentAccessionNum,
        vaf,
        value,
      }

      if (listType === 'simple') {
        switch (priority.trim().toUpperCase()) {
          case 'YES':
            displayAlterations1.value.push(obj)
            break
          case 'MAYBE':
            displayAlterations2.value.push(obj)
            break
          case 'UNKNOWN':
            displayAlterations3.value.push(obj)
            break
          default:
            break
        }
      } else {
        // always Tier 1
        displayAlterations1.value.push(obj)
      }
    }
  }

  loadDisplayObjects(altArray, 'simple')
  loadDisplayObjects(fusionArray, 'fusions')
  loadDisplayObjects(rnaArray, 'rna')
  loadDisplayObjects(copyArray, 'cnv')
  loadDisplayObjects(hlaArray, 'hla')
}

// OLD
export const loadAllGenomicsOld = async ({
  accessionNum,
  alterations,
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  getTreatmentsTrialsFirstAlteration,
  getTreatmentsTrials,
  hlaTypes,
  loadingGenomics,
  multiComparison,
  msi,
  rnaAnnotations,
  sampleId,
  spinnerOn,
  tierButtonClick,
  tmb,
  treatments,
  trials,
  variants,
}) => {
  // const timerStart = performance.now()
  loadingGenomics.value = true
  spinnerOn.value = true
  tierButtonClick(1)
  msi.value = ''
  tmb.value = ''
  const multiComparisonVariants = []
  const multiComparisonFusions = []
  const multiComparisonRna = []
  const multiComparisonCopy = []
  const multiComparisonHla = []

  if (multiComparison.value) {
    let multiComparisonVariantsMerged = []
    let multiComparisonFusionsMerged = []
    let multiComparisonRnaMerged = []
    let multiComparisonCopyMerged = []
    let multiComparisonHlaMerged = []

    for (const sample of sampleId.value) {
      // const clinicalTimerStart = performance.now()
      const [results, fusionResults, rnaAnnotationResults, copyNumberResults, hlaResults]
        = await Promise.all([getVariants(sample.sample), getFusions(sample.sample),
          getRnaAnnotation(sample.sample), getCopyNumber(sample.sample), getHla(sample.sample)])
      // const clinicalTimerEnd = performance.now()
      // console.log(`old genomics promise.All() call time (one sample of multi): ${clinicalTimerEnd - clinicalTimerStart} ms`)
      multiComparisonVariants.push(results.results)
      multiComparisonVariantsMerged = [...multiComparisonVariantsMerged, ...results.results]
      multiComparisonFusions.push(fusionResults.results)
      multiComparisonFusionsMerged = [...multiComparisonFusionsMerged, ...fusionResults.results]
      multiComparisonRna.push(rnaAnnotationResults.results)
      multiComparisonRnaMerged = [...multiComparisonRnaMerged, ...rnaAnnotationResults.results]
      multiComparisonCopy.push(copyNumberResults.results)
      multiComparisonCopyMerged = [...multiComparisonCopyMerged, ...copyNumberResults.results]
      multiComparisonHla.push(hlaResults.results)
      multiComparisonHlaMerged = [...multiComparisonHlaMerged, ...hlaResults.results]
    }
    variants.value = multiComparisonVariantsMerged
    fusions.value = multiComparisonFusionsMerged
    rnaAnnotations.value = multiComparisonRnaMerged
    copyNumberVars.value = multiComparisonCopyMerged
    hlaTypes.value = multiComparisonHlaMerged
  }
  else {
    // const clinicalTimerStart = performance.now()
    const [results, fusionResults, rnaAnnotationResults, copyNumberResults, hlaResults]
      = await Promise.all([getVariants(sampleId.value), getFusions(sampleId.value),
        getRnaAnnotation(sampleId.value), getCopyNumber(sampleId.value), getHla(sampleId.value)])
    // const clinicalTimerEnd = performance.now()
    // console.log(`old genomics promise.All() call time (single sample): ${clinicalTimerEnd - clinicalTimerStart} ms`)
    variants.value = results.results
    fusions.value = fusionResults.results
    rnaAnnotations.value = rnaAnnotationResults.results
    copyNumberVars.value = copyNumberResults.results
    hlaTypes.value = hlaResults.results
  }

  // Note: filtering out cases where ref_allele == genome
  const filteredVariants = unref(variants).filter((variant) => variant.Ref_allele !== variant.Genotype)

  const variantToPromiseMap = new Map()
  const MAX_RETRIES = 3 // Maximum number of retries
  // clear out the alterations array
  alterations.value = []
  treatments.value = []
  trials.value = []
  const variantsThatNeedVep = []

  // We need Gene and either Protein Change or Coding change, or else need to VEP annotate
  filteredVariants.forEach(variant => {
    if (!variant.Protein_change || !variant.Coding_change || !variant.Gene) {
      variantsThatNeedVep.push(variant)
    } else if ((variant.Protein_change[0] === '.' && variant.Coding_change[0] === '.') || variant.Gene[0] === '.') {
      variantsThatNeedVep.push(variant)
    } else {
      alterations.value.push(variant)
    }
  })

  // Promise style, so we can do these VEP calls
  // in parallel and get a speedup via Promise.allSettled
  const getAlterationsRetry = async (variant, retries) => {
    try {
      const response = await getAlterations(variant)
      return response
    } catch (error) {
      if (retries > 0) {
        console.log(`Retrying for variant ${variant}, retries left: ${retries}`)
        return getAlterationsRetry(variant, retries - 1)
      } else {
        throw error // Throw if all retries are exhausted
      }
    }
  }

  variantsThatNeedVep.forEach(variant => {
    const promise = getAlterationsRetry(variant, MAX_RETRIES)
    variantToPromiseMap.set(variant, promise)
  })

  // TODO: eventually the omicsDS API will handle the cases where there is no
  // alteration, and we won't do VEP Annotation at all
  let retryCounter = 0
  const settleVepPromises = async (variants, array) => {
    const retryPromises = []
    const retryVariants = []
    await Promise.allSettled(array)
      .then(results => {
        results.forEach((result, index) => {
          const variant = variants[index]
          if (result.status === 'fulfilled') {
            const response = result.value
            if (!response?.results?.length) { return }
            // we're getting 'annotation: failed' here, so retry
            if (response.results[0].annotation) {
              retryPromises.push(getAlterationsRetry(variant, MAX_RETRIES))
              retryVariants.push(variants[index])
            } else {
              const alter = response.results[0]
              if (!alter?.name) { return }
              //update the existing variant
              const nameArr = alter?.name?.split(' ')
              const gene = nameArr[0]
              const alt = nameArr[1]
              variant.Gene = []
              variant.Gene.push(gene)
              variant.Protein_change = []
              variant.Protein_change.push(alt)
              alterations.value.push(variant)
            }
          } else {
            console.log(result.status, result.reason)
          }
        })
      })
    // TODO: do we we even want this logic? some seem to always fail in which
    // case you absolutely must have the counter to break the recursion
    if (retryVariants.length && retryCounter < MAX_RETRIES) {
      retryCounter += 1
      await settleVepPromises(retryVariants, retryPromises)
    }
  }

  await settleVepPromises(variantsThatNeedVep,
    Array.from(variantToPromiseMap.values()))

  //The sample flagging for multi-sample comparison
  if (multiComparison.value) {

    const flagSamples = (itemList, multiComparisonList, isVariants) => {
      //deduplicate
      if (isVariants) {
        itemList.value = toRaw(unref(itemList)).filter((variant, index, array) =>
          array.findIndex(t => {
            if (variant.Protein_change[0] === '.') {
              return t.Gene[0] === variant.Gene[0] && t.Coding_change[0] === variant.Coding_change[0] && t.VP === variant.VP
            }
            return t.Gene[0] === variant.Gene[0] && t.Protein_change[0] === variant.Protein_change[0] && t.VP === variant.VP
          }) === index)
      } else {
        itemList.value = toRaw(unref(itemList)).filter((item, index, array) => array.findIndex(t => t.value === item.value) === index)
      }

      for (const item of toRaw(unref(itemList))) {
        multiComparisonList.forEach((array, index) => {
          if (isVariants) {
            if (array.find(e => {
              if (item.Protein_change === '.') {
                return e.Gene[0] === item.Gene[0] && e.Coding_change[0] === item.Coding_change[0] && item.VP === e.VP
              }
              return e.Gene[0] === item.Gene[0] && e.Protein_change[0] === item.Protein_change[0] && item.VP === e.VP
            })) {
              item[`Sample_${index + 1}`] = true
            }
          } else {
            if (array.find(e => e.value === item.value)) {
              item[`Sample_${index + 1}`] = true
            }
          }
        })
      }
    }

    flagSamples(alterations, multiComparisonVariants, true)
    flagSamples(fusions, multiComparisonFusions, false)
    flagSamples(rnaAnnotations, multiComparisonRna, false)
    flagSamples(copyNumberVars, multiComparisonCopy, false)
    flagSamples(hlaTypes, multiComparisonHla, false)
  }

  //The multi cards get TMB/MSI themselves
  if (!multiComparison.value) {
    // await getTMBMSI()
    const msiResults = await getTmbMsi(sampleId.value, 'MSI')
    msi.value = msiResults.results && msiResults.results[0]?.value ? msiResults.results[0].value : 'No clinically significant MSI'

    const tmbResults = await getTmbMsi(sampleId.value, 'TMB')
    tmb.value = tmbResults.results && tmbResults.results[0]?.value ? tmbResults.results[0].value : 'No clinically significant TMB'
  }

  await loadDisplayDataOld({
    accessionNum,
    displayAlterations1,
    displayAlterations2,
    displayAlterations3,
    alterations,
    copyNumberVars,
    fusions,
    hlaTypes,
    rnaAnnotations,
  })

  //get the first
  await getTreatmentsTrialsFirstAlteration()

  //get the rest
  await getTreatmentsTrials()
  loadingGenomics.value = false
  spinnerOn.value = false
  // const timerEnd = performance.now()
  // console.log(`loadAllGenomicsOld time: ${timerEnd - timerStart} ms`)
}

export const recodeAminoAcid = (aminoAcid) => {
  aminoAcid = aminoAcid.replace(/Ala/g, 'A')
  aminoAcid = aminoAcid.replace(/Arg/g, 'R')
  aminoAcid = aminoAcid.replace(/Asp/g, 'D')
  aminoAcid = aminoAcid.replace(/Asn/g, 'N')
  aminoAcid = aminoAcid.replace(/Cys/g, 'C')
  aminoAcid = aminoAcid.replace(/Glu/g, 'E')
  aminoAcid = aminoAcid.replace(/Gln/g, 'Q')
  aminoAcid = aminoAcid.replace(/Gly/g, 'G')
  aminoAcid = aminoAcid.replace(/His/g, 'H')
  aminoAcid = aminoAcid.replace(/Ile/g, 'I')
  aminoAcid = aminoAcid.replace(/Leu/g, 'L')
  aminoAcid = aminoAcid.replace(/Lys/g, 'K')
  aminoAcid = aminoAcid.replace(/Met/g, 'M')
  aminoAcid = aminoAcid.replace(/Phe/g, 'F')
  aminoAcid = aminoAcid.replace(/Pro/g, 'P')
  aminoAcid = aminoAcid.replace(/Ser/g, 'S')
  aminoAcid = aminoAcid.replace(/Thr/g, 'T')
  aminoAcid = aminoAcid.replace(/Trp/g, 'W')
  aminoAcid = aminoAcid.replace(/Tyr/g, 'Y')
  aminoAcid = aminoAcid.replace(/Val/g, 'V')
  aminoAcid = aminoAcid.replace(/Ter/g, '*')
  return (aminoAcid)
}

// This is needed because, particularly for multisample-comparison
// we need to know which accession number to use. The most recent is ideal.
export const getMostRecentAccessionNum = (accessionNum, sampleFlags) => {
  try {
    //Because accessionNum can be a string for single sample, or an array of accessionNum/date pairs for multi
    if (typeof accessionNum === 'string') { return accessionNum }
    const rawSampleFlags = toRaw(unref(sampleFlags))
    const accessionMultiComparison = accessionNum.sort(compareDatesDescending)
    // Use the first (most recent) sampleID that is flagged for the given alteration
    for (let index = 0; index < rawSampleFlags.length; index++) {
      if (rawSampleFlags[index]) { return accessionMultiComparison[index].accessionNum }
    }
  }
  catch (e) {
    console.log('Error obtaining most recent sampleId: ', e)
    return ''
  }
  return ''
}

const loadDisplayDataOld = async ({
  accessionNum,
  alterations,
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  hlaTypes,
  rnaAnnotations,
}) => {
  clearCenterColumn('genomics')
  displayAlterations1.value = []
  displayAlterations2.value = []
  displayAlterations3.value = []
  const altArray = toRaw(unref(alterations))
  const fusionArray = toRaw(unref(fusions))
  const rnaArray = toRaw(unref(rnaAnnotations))
  const copyArray = toRaw(unref(copyNumberVars))
  const hlaArray = toRaw(unref(hlaTypes))

  const loadDisplayObjects = (rawArray, listType = 'other-non-simple') => {
    for (const item of rawArray) {
      const exon = item.exon || ''
      const priority = item.VP || item.priority || ''
      const priorityInt = parseInt(priority, 10)
      const sample1 = item.Sample_1 || false
      const sample2 = item.Sample_2 || false
      const sample3 = item.Sample_3 || false
      const mostRecentAccessionNum = getMostRecentAccessionNum(accessionNum.value, [sample1, sample2, sample3])
      let gene = ''
      let alt = ''

      const typeMap = {
        'simple': 'small_variant',
        'fusions': 'fusion',
        'cnv': 'cnv',
        'rna': 'rna_annotation',
        'hla': 'hla',
        'stablity': 'stability',
      }
      const type = typeMap[listType] || ''

      if (listType === 'simple') {
        gene = item.Gene[0]
        alt = item.Protein_change[0] !== '.' ? item.Protein_change[0].replace('p.', '') : item.Coding_change[0]
      } else if (listType === 'fusions') {
        gene = item.value ? item.value.replace('::', '-') : ''
        alt = ''
      } else {
        const tokens = item.value.split(';')
        gene = tokens[0]
        alt = tokens.slice(1, tokens.length).toString().replace(',', ' ')
      }

      const obj = {
        gene,
        alteration: listType === 'simple' ? recodeAminoAcid(alt) : alt,
        originalAlt: alt,
        exon,
        type,
        priority,
        treatmentsCount: 0,
        trialsCount: 0,
        treatmentRows: [],
        trialRows: [],
        sample1,
        sample2,
        sample3,
        mostRecentAccessionNum,
        value: `${gene} ${recodeAminoAcid(alt)}`,
      }

      if (listType === 'simple') {
        switch (!isNaN(priorityInt) && priorityInt) {
          // The NaN case here represents null or unknown in which case
          // we put in Tier 3 'Unknown'
          case 1:
            displayAlterations1.value.push(obj)
            break
          case 2:
            displayAlterations2.value.push(obj)
            break
          case 3:
          case false:
            displayAlterations3.value.push(obj)
            break
          default:
            // priority 4 (no significance) or any other numerical value should be ignored
            break
        }
      } else {
        // always Tier 1
        displayAlterations1.value.push(obj)
      }
    }
  }

  loadDisplayObjects(altArray, 'simple')
  loadDisplayObjects(fusionArray, 'fusions')
  loadDisplayObjects(rnaArray, 'rna')
  loadDisplayObjects(copyArray, 'cnv')
  loadDisplayObjects(hlaArray, 'hla')
}
